import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ConnectTherapist = () => {
  const [invitationCode, setInvitationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const token = localStorage.getItem('therapy_auth_token');
      if (!token) {
        setError('Du måste vara inloggad för att använda en inbjudningskod.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/invitations/use-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          invitation_code: invitationCode.toUpperCase().trim()
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(`🎉 Grattis! Du är nu ansluten till ${data.therapist.name}!`);
        // Redirect to dashboard after success
        setTimeout(() => {
          navigate('/client');
        }, 2000);
      } else {
        setError(data.detail || 'Ogiltigt eller utgånget inbjudningskod.');
      }
    } catch (err) {
      setError('Ett fel uppstod. Försök igen senare.');
      console.error('Invitation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatInvitationCode = (value) => {
    // Remove non-alphanumeric characters and convert to uppercase
    const cleaned = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    // Add spaces every 2 characters for better readability
    return cleaned.replace(/(.{2})/g, '$1 ').trim();
  };

  const handleInputChange = (e) => {
    const formatted = formatInvitationCode(e.target.value);
    setInvitationCode(formatted);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-600 to-indigo-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Anslut till terapeut</h1>
          <p className="text-gray-600">Ange din inbjudningskod för att komma igång</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-blue-900 mb-1">💙 Har du fått en inbjudan?</h3>
              <p className="text-sm text-blue-800">
                Din terapeut har skickat dig en 8-siffrig kod via e-post. 
                Ange den här för att få tillgång till säker terapi.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="invitationCode" className="block text-sm font-semibold text-gray-700 mb-2">
              Inbjudningskod
            </label>
            <input
              type="text"
              id="invitationCode"
              value={invitationCode}
              onChange={handleInputChange}
              maxLength="11" // 8 chars + 3 spaces
              placeholder="XX XX XX XX"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 text-center text-lg font-mono tracking-wider"
              style={{ letterSpacing: '0.2em' }}
            />
            <p className="text-xs text-gray-500 mt-1 text-center">
              Ange den 8-siffriga koden du fick via e-post
            </p>
          </div>

          <button
            type="submit"
            disabled={loading || invitationCode.replace(/\s/g, '').length !== 8}
            className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Ansluter...
              </div>
            ) : (
              'Anslut till terapeut'
            )}
          </button>
        </form>

        {message && (
          <div className="mt-4 p-4 rounded-lg bg-green-50 border border-green-200">
            <p className="text-green-800 font-medium text-center">{message}</p>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 rounded-lg bg-red-50 border border-red-200">
            <p className="text-red-800 font-medium text-center">{error}</p>
          </div>
        )}

        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600 mb-4">
            Har du ingen inbjudningskod?
          </p>
          <button
            onClick={() => navigate('/find-therapist')}
            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
          >
            Hitta en terapeut själv →
          </button>
        </div>

        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-semibold text-gray-800 mb-2">🔐 Din säkerhet</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• End-to-end krypterad kommunikation</li>
            <li>• GDPR-kompatibel plattform</li>
            <li>• Endast du och din terapeut ser era samtal</li>
          </ul>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            ← Tillbaka till inloggning
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConnectTherapist;
